import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { API_URL, db } from ".."; // Import db from index.js
import { ref, push, onValue, off, update,set } from "firebase/database"; // Modular Firebase imports

const FloatingChat = ({ jobId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [driverId, setDriverId] = useState(null);
  const [affiliateId, setAffiliateId] = useState(null);
  const messagesEndRef = useRef(null);

  // Reference to messages in Firebase Realtime Database
  const messagesRef = ref(db, `messages/${jobId}`);

  useEffect(() => {
    fetchSingle();
    setupFirebaseListener();

    // Get current user from localStorage
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setCurrentUser(user);
    }

    return () => {
      // Clean up Firebase listener when component unmounts
      off(messagesRef);
    };
  }, [jobId]);

  const fetchSingle = async () => {
    try {
      const response = await fetch(`${API_URL}/api/job/affiliate/fetch-single/${jobId}`);
      const data = await response.json();
      if (response.status === 200 && data.data?.driver) {
        setDriverId(data.data.driver_id);
        setAffiliateId(data.data.assigned_to);
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
    }
  };

  const setupFirebaseListener = () => {
    onValue(
      messagesRef,
      (snapshot) => {
        const messagesData = snapshot.val();
        if (messagesData) {
          const messagesArray = Object.keys(messagesData).map((key) => ({
            id: key,
            ...messagesData[key],
          }));
          const sortedMessages = messagesArray.sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
          );
          setMessages(sortedMessages);
        } else {
          setMessages([]);
        }
      },
      {
        onlyOnce: false, // Ensures real-time updates
      }
    );
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentUser) return;

    const newMessageData = {
      job_id: jobId,
      message: newMessage,
      sender_id: currentUser.id,
      receiver_id: currentUser.role === "owner" ? driverId : affiliateId,
      createdAt: new Date().toISOString(),
      status: "sent",
    };
    console.log(newMessageData)
    try {
      const newMessageRef = push(messagesRef); // Push new message
      await set(newMessageRef, newMessageData); // ✅ Correct usage: pass the reference AND data
      await update(newMessageRef, { id: newMessageRef.key, status: "read" }); // Update with ID and status
      setNewMessage("");
      scrollToBottom();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div>
      {/* Floating Chat Button */}
      <button
          className="btn btn-primary rounded-circle position-fixed bottom-0 start-140 m-4 p-3 shadow"

        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "60px",
          height: "60px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon icon="bi:chat-dots-fill" width="28" height="28" />
      </button>

      {/* Chat Box */}
      {isOpen && (
        <div
          className="card position-fixed bottom-0 start-140 m-4 shadow-lg"
          style={{ width: "350px", height: "500px", zIndex: 9000 }}
        >
          {/* Header */}
          <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <span>Chat With Driver</span>
            <button
              className="btn btn-sm btn-light"
              onClick={() => setIsOpen(false)}
            >
              <Icon icon="ic:baseline-close" width="20" height="20" />
            </button>
          </div>

          {/* Messages Area */}
          <div
            className="card-body overflow-auto d-flex flex-column"
            style={{ height: "400px", zIndex: 9000 }}
          >
            {messages.length === 0 ? (
              <p className="text-muted text-center">No messages yet</p>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`d-flex mb-2 ${
                    msg.sender_id === currentUser?.id
                      ? "justify-content-end"
                      : "justify-content-start"
                  }`}
                >
                  <div
                    className={`p-2 rounded shadow-sm ${
                      msg.sender_id === currentUser?.id
                        ? "bg-primary text-white"
                        : "bg-light text-dark"
                    }`}
                    style={{
                      maxWidth: "75%",
                      borderRadius: "18px",
                      padding: "8px 12px",
                      fontSize: "14px",
                      position: "relative",
                    }}
                  >
                    {msg.message}
                    <div
                      style={{
                        fontSize: "10px",
                        marginTop: "4px",
                        textAlign: "right",
                        color:
                          msg.sender_id === currentUser?.id ? "#ddd" : "#666",
                      }}
                    >
                      {formatTime(msg.createdAt)}{" "}
                      {msg.status === "read" ? "✔✔" : "✔"}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef}></div>
          </div>

          {/* Input Box */}
          <div className="card-footer p-2">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyPress}
              />
              <button className="btn btn-primary" onClick={sendMessage}>
                <Icon icon="bi:send-fill" width="20" height="20" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingChat;